/**
 * The find that is waiting to be shown (docs/trinkets.md): a completion that turned up a trinket
 * sets it here before the task screen leaves for the board, and the workshop's dig sets it too; the
 * shell (src/app.tsx) draws the find sheet over whatever screen is up while one waits and the flag
 * is on. One at a time: a second find before the first is closed waits its turn.
 */
import { useEffect, useState } from "preact/hooks";
import type { TrinketFind } from "../shared/trinkets.ts";

const EVENT = "op:trinket-found";
let queue: TrinketFind[] = [];

/** Queues a find for the sheet. */
export function announceFind(find: TrinketFind): void {
  queue = [...queue, find];
  if (typeof window !== "undefined") window.dispatchEvent(new Event(EVENT));
}

/** The find up now, or null. */
export function pendingFind(): TrinketFind | null {
  return queue[0] ?? null;
}

/** Closes the find up now; the next, if any, comes up. */
export function closeFind(): void {
  queue = queue.slice(1);
  if (typeof window !== "undefined") window.dispatchEvent(new Event(EVENT));
}

/** Drops every waiting find (a sign-out). */
export function resetFinds(): void {
  queue = [];
}

/** The find up now, live. */
export function usePendingFind(): TrinketFind | null {
  const [find, setFind] = useState(pendingFind);
  useEffect(() => {
    const update = () => setFind(pendingFind());
    window.addEventListener(EVENT, update);
    update();
    return () => window.removeEventListener(EVENT, update);
  }, []);
  return find;
}
